import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Thermometer, AlertTriangle, CheckCircle2, Download, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function TemperatureHistory() {
  const [logs, setLogs] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [distFilter, setDistFilter] = useState("__all__");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const [logsR, distsR] = await Promise.all([
      api.get("/scan/temperature", { params: { limit: 500 } }),
      api.get("/distributors"),
    ]);
    setLogs(logsR.data);
    setDistributors(distsR.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    return distFilter === "__all__" ? logs : logs.filter((l) => l.distributor_id === distFilter);
  }, [logs, distFilter]);

  // KPIs
  const stats = useMemo(() => {
    const total = filtered.length;
    const outOfRange = filtered.filter((l) => l.out_of_range).length;
    const avg = total > 0 ? filtered.reduce((s, l) => s + l.temperature_c, 0) / total : null;
    const last30 = filtered.filter((l) => {
      const d = new Date(l.created_at);
      return (Date.now() - d.getTime()) < 30 * 86400000;
    });
    return { total, outOfRange, avg, last30Count: last30.length };
  }, [filtered]);

  // Export CSV
  const exportCsv = () => {
    const header = "Date;Distributeur;Température (°C);Statut;Notes\n";
    const rows = filtered.map((l) => {
      const dt = new Date(l.created_at).toLocaleString("fr-CH");
      const statut = l.out_of_range ? "HORS PLAGE (>5°C)" : "OK";
      const notes = (l.notes || "").replace(/"/g, '""');
      return `"${dt}";"${l.distributor_name || ""}";"${l.temperature_c.toFixed(1).replace(".", ",")}";"${statut}";"${notes}"`;
    }).join("\n");
    const blob = new Blob(["\uFEFF" + header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `temperatures_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="temperature-history-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/scan" className="text-xs uppercase tracking-[0.2em] text-[#737373] hover:text-[#1F4E3D] flex items-center gap-1.5" data-testid="back-to-scan">
            <ArrowLeft className="w-3.5 h-3.5" /> Scan terrain
          </Link>
          <span className="text-[#D4D4D0]">/</span>
          <h2 className="text-xl font-heading flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-[#1F4E3D]" /> Historique températures
          </h2>
        </div>
        <Button onClick={exportCsv} disabled={filtered.length === 0} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="btn-export-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV (SCAV)
        </Button>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E5E0] rounded p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">Total relevés</div>
          <div className="text-2xl font-heading mt-1">{stats.total}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">30 derniers jours</div>
          <div className="text-2xl font-heading mt-1">{stats.last30Count}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">Moyenne</div>
          <div className="text-2xl font-heading mt-1">{stats.avg != null ? `${stats.avg.toFixed(1)}°C` : "—"}</div>
        </div>
        <div className="bg-white border rounded p-4" style={{ borderColor: stats.outOfRange > 0 ? "#E54D2E" : "#E5E5E0" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">Hors plage ({">"}5°C)</div>
          <div className="text-2xl font-heading mt-1" style={{ color: stats.outOfRange > 0 ? "#E54D2E" : "inherit" }}>{stats.outOfRange}</div>
        </div>
      </div>

      {/* Filtre */}
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-[0.2em] text-[#737373]">Distributeur</span>
        <Select value={distFilter} onValueChange={setDistFilter}>
          <SelectTrigger className="bg-white w-72" data-testid="select-dist-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tous</SelectItem>
            {distributors.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tableau */}
      <div className="bg-white border border-[#E5E5E0] rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F0] border-b border-[#E5E5E0]">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] text-[#737373]">Date</th>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] text-[#737373]">Distributeur</th>
              <th className="px-4 py-3 text-right text-[10px] uppercase tracking-[0.2em] text-[#737373]">Température</th>
              <th className="px-4 py-3 text-center text-[10px] uppercase tracking-[0.2em] text-[#737373]">Statut</th>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] text-[#737373]">Notes</th>
            </tr>
          </thead>
          <tbody data-testid="temp-table-body">
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#737373]">Chargement…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-[#737373]">Aucun relevé. Allez sur <Link to="/scan" className="text-[#1F4E3D] underline">Scan terrain</Link> pour saisir le premier.</td></tr>
            ) : filtered.map((l) => (
              <tr key={l.id} className="border-b border-[#E5E5E0] hover:bg-[#FAFAF7]" data-testid={`temp-row-${l.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{new Date(l.created_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="px-4 py-3">{l.distributor_name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono font-medium" style={{ color: l.out_of_range ? "#E54D2E" : "#1F4E3D" }}>
                  {l.temperature_c.toFixed(1)} °C
                </td>
                <td className="px-4 py-3 text-center">
                  {l.out_of_range ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-[#E54D2E]/10 text-[#E54D2E]">
                      <AlertTriangle className="w-3 h-3" /> Hors plage
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-[#88A066]/10 text-[#5A7045]">
                      <CheckCircle2 className="w-3 h-3" /> OK
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#737373]">{l.notes || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-[#737373]">
        Les relevés sont archivés pour les contrôles SCAV (Service de la consommation et des affaires vétérinaires). Export CSV au format UTF-8 BOM, ouvrable directement dans Excel.
      </p>
    </div>
  );
}
